import React, { useState } from 'react';
import { Table } from '../../Component/Table';
import { useTranslation } from 'react-i18next';
import { Button, Space, Popconfirm, message } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Api from '../../Network';
import CustomerTypeModal from './CustomerTypeModal';
import NameByLang from '../../Component/nameByLang';
import { usePermissions } from '../../contexts/PermissionContext';
import { AnimatedContainer } from '../../Component/ui';
import './style.scss';

const CustomerTypes = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [selectedCustomerType, setSelectedCustomerType] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  // Delete customer type mutation
  const deleteCustomerTypeMutation = useMutation({
    mutationFn: (customerTypeId: number) =>
      Api.delete(`/api/admin/customer-types/${customerTypeId}`),
    onSuccess: () => {
      message.success(t('customerTypes.deleteSuccess', 'Customer type deleted successfully'));
      queryClient.invalidateQueries({ queryKey: ['/api/admin/customer-types'] });
    },
    onError: (_error: any) => {
      message.error(t('common.error', 'Error deleting customer type'));
    },
  });

  const handleDelete = (customerTypeId: number) => {
    deleteCustomerTypeMutation.mutate(customerTypeId);
  };

  const handleEdit = (record: any) => {
    setSelectedCustomerType(record);
    setModalMode('edit');
    setModalVisible(true);
  };

  const handleAddNew = () => {
    setSelectedCustomerType(null);
    setModalMode('create');
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedCustomerType(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/admin/customer-types'] });
  };

  return (
    <AnimatedContainer className="customer-types-page" variant="fadeUp">
      <Table
        tableQueryKey="/api/admin/customer-types"
        tableTitle={t('customerTypes.title', 'Customer Types')}
        showSearch={true}
        hideSearch={true}
        showDeleteSwitch={false}
        hideFilters={true}
        rowKey="id"
        createPermission="customer_types.create"
        tableFilter={{
          filters: {},
          paging: { page: 1, pageSize: 20 },
          sorting: {},
        }}
        onTableFilterChange={(_path: string, _value: any) => {
          // Handle table filter changes if needed
        }}
        columns={[
          {
            title: t('customerTypes.columns.nameEn', 'Name (EN)'),
            dataIndex: 'name_en',
            key: 'name_en',
            render: (name: string) => <NameByLang name={name} className="bold primary" />,
          },
          {
            title: t('customerTypes.columns.nameAr', 'Name (AR)'),
            dataIndex: 'name_ar',
            key: 'name_ar',
            render: (name: string) => <NameByLang name={name} className="bold primary" />,
          },
          {
            title: t('customerTypes.columns.name', 'Name'),
            key: 'name',
            render: (_: any, record: any) => (
              <NameByLang
                name_en={record.name_en}
                name_ar={record.name_ar}
                className="bold primary"
              />
            ),
          },
          {
            title: t('common.actions', 'Actions'),
            key: 'actions',
            render: (_: any, record: any) => (
              <Space>
                {(hasPermission('update_customer_type') ||
                  hasPermission('customer_types.update') ||
                  hasPermission('edit_customer_type')) && (
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                    size="small"
                  >
                    {t('common.edit', 'Edit')}
                  </Button>
                )}
                {(hasPermission('delete_customer_type') ||
                  hasPermission('customer_types.delete')) && (
                  <Popconfirm
                    title={t(
                      'customerTypes.deleteConfirm',
                      'Are you sure you want to delete this customer type?'
                    )}
                    description={t('customerTypes.deleteWarning', 'This action cannot be undone.')}
                    onConfirm={() => handleDelete(record.id)}
                    okText={t('common.yes', 'Yes')}
                    cancelText={t('common.no', 'No')}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} size="small">
                      {t('common.delete', 'Delete')}
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
        buttonContent={
          hasPermission('create_customer_type') || hasPermission('customer_types.create')
            ? t('customerTypes.addNew', 'Add New Customer Type')
            : undefined
        }
        onClickNewButton={
          hasPermission('create_customer_type') || hasPermission('customer_types.create')
            ? handleAddNew
            : undefined
        }
      />

      <CustomerTypeModal
        visible={modalVisible}
        mode={modalMode}
        customerType={selectedCustomerType}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </AnimatedContainer>
  );
};

export default CustomerTypes;
